import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Tag, Button, Space, message, Modal, Select, Input } from 'antd';
import { EyeOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import './DemoRequests.css';

const { TextArea } = Input;

function DemoRequests() {
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);

  const fetchDemoRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get(`/demo-requests${params}`);
      setDemoRequests(response.data || []);
    } catch (error) {
      console.error('Error fetching demo requests:', error);
      message.error('Failed to load demo requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchDemoRequests();
  }, [fetchDemoRequests]);

  const updateStatus = async (id, newStatus) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/demo-requests/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (!response.ok) throw new Error('Failed to update');
      message.success('Status updated successfully');
      fetchDemoRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      message.error('Failed to update status');
    }
  };

  const showDetails = (record) => {
    setSelectedRequest(record);
    setDetailsVisible(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      contacted: 'blue',
      scheduled: 'cyan',
      completed: 'green',
      cancelled: 'red'
    };
    return colors[status] || 'default';
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => a.full_name.localeCompare(b.full_name)
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      sorter: (a, b) => a.company_name.localeCompare(b.company_name)
    },
    {
      title: 'Email',
      dataIndex: 'work_email',
      key: 'work_email'
    },
    {
      title: 'Fleet Size',
      dataIndex: 'fleet_size',
      key: 'fleet_size',
      render: (size) => size || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status.toUpperCase()}
        </Tag>
      ),
      filters: [
        { text: 'Pending', value: 'pending' },
        { text: 'Contacted', value: 'contacted' },
        { text: 'Scheduled', value: 'scheduled' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => showDetails(record)}
          >
            View
          </Button>
          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                size="small"
                icon={<CheckOutlined />}
                onClick={() => updateStatus(record.id, 'contacted')}
              >
                Mark Contacted
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <div className="demo-requests-container">
      <Card
        title="Demo Requests"
        extra={
          <Space>
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 150 }}
              onChange={setStatusFilter}
              value={statusFilter}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="contacted">Contacted</Select.Option>
              <Select.Option value="scheduled">Scheduled</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchDemoRequests}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={demoRequests}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} requests`
          }}
        />
      </Card>

      <Modal
        title="Demo Request Details"
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setDetailsVisible(false)}>
            Close
          </Button>,
          selectedRequest?.status !== 'completed' && selectedRequest?.status !== 'cancelled' && (
            <Select
              key="status"
              placeholder="Update Status"
              style={{ width: 150 }}
              onChange={(value) => {
                updateStatus(selectedRequest.id, value);
                setDetailsVisible(false);
              }}
            >
              <Select.Option value="pending">Pending</Select.Option>
              <Select.Option value="contacted">Contacted</Select.Option>
              <Select.Option value="scheduled">Scheduled</Select.Option>
              <Select.Option value="completed">Completed</Select.Option>
              <Select.Option value="cancelled">Cancelled</Select.Option>
            </Select>
          )
        ]}
      >
        {selectedRequest && (
          <div className="demo-request-details">
            <div className="detail-row">
              <strong>Full Name:</strong>
              <span>{selectedRequest.full_name}</span>
            </div>
            <div className="detail-row">
              <strong>Work Email:</strong>
              <span>{selectedRequest.work_email}</span>
            </div>
            <div className="detail-row">
              <strong>Company Name:</strong>
              <span>{selectedRequest.company_name}</span>
            </div>
            <div className="detail-row">
              <strong>Phone:</strong>
              <span>{selectedRequest.phone || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <strong>Fleet Size:</strong>
              <span>{selectedRequest.fleet_size || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <strong>Status:</strong>
              <Tag color={getStatusColor(selectedRequest.status)}>
                {selectedRequest.status.toUpperCase()}
              </Tag>
            </div>
            <div className="detail-row">
              <strong>Submitted:</strong>
              <span>{new Date(selectedRequest.created_at).toLocaleString()}</span>
            </div>
            {selectedRequest.message && (
              <div className="detail-row full-width">
                <strong>Message:</strong>
                <TextArea
                  value={selectedRequest.message}
                  readOnly
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default DemoRequests;
